<template>
  <div class="p-2 flex items-start justify-between w-full gap-6">
    <!-- LEFT SIDE -->
    <div class="flex items-start gap-1 relative">
      <div class="flex items-center gap-1">
        <!-- Navigate Back Button -->
        <button class="p-1 pr-2 hover:bg-background-normal truncate-group-trigger rounded-md flex items-center gap-1"
        @click="navigateBack">
          <ChevronIcon class="transform rotate-90" />
          <div class="w-8 h-8 rounded-md flex items-center justify-center">
            <img v-if="lens && lens.avatar" :src="lens.avatar" class="w-full h-full object-cover rounded-md">
            <div v-else class="w-full h-full flex items-center justify-center rounded-md"
            :class="{'bg-neutral-400': !loading && lens.name}">
              <span v-if="!loading && lens.name" class="text-background-hover text-label-xs font-medium">
                {{ getInitials(lens.name) }}
              </span>
              <div v-else-if="loading" class="w-full h-full skeleton rounded-md" />
            </div>
          </div>
        </button>
        <!-- Lens Name -->
        <div v-if="lens?.name" class="truncate-group-tooltip transform px-1.5 py-1 rounded-md absolute shadow-md space-y-1">
          <BaseText size="xs" class="text-white">
            {{ lens.name }}
          </BaseText>
        </div>
        <!-- Gap Line -->
        <div class="w-px h-5 bg-border-normal rounded-sm mx-1" />
      </div>
      <!-- Report Switcher and Report Description -->
      <div class="flex flex-wrap pt-1 gap-y-1" style="min-height: 40px;">
        <!-- Report Route Switcher -->
        <div v-if="loading" class="h-7 w-16 skeleton rounded-md" />
        <BaseDropdownRouteSwitcher
          v-else
          :current-route="currentRoute"
          :routes="reportRoutes"
        />
        <!-- Report Description -->
        <div class="pl-2">
          <div class="flex items-center" style="min-height: 32px;">
            <div v-if="loading" class="h-7 w-40 skeleton rounded-md" />
            <BaseText v-else size="sm" class="text-icon-normal fade-in">
              {{ description }}
            </BaseText>
          </div>
        </div>
      </div>
    </div>
    <!-- RIGHT SIDE -->
    <div class="h-10 flex items-center gap-2 flex-shrink-0">
      <!-- Data Health Button -->
      <DataHealthButton 
        v-if="lens && !isDemoLens"
        :last-refreshed-at="lens?.last_refreshed_at"
        :date-range="selectedDateRange"
        @refreshComplete="$emit('refreshComplete')"
      />
      <!-- Attribution Window Selector (hidden on the demo Lens: sample data has no attribution) -->
      <div v-if="getMetricsPlatform === 'meta' && !isDemoLens" class="relative" v-on-clickaway="() => { show.attributionWindow = false }">
        <div v-if="loading" class="relative top-0.5 h-8 skeleton rounded-md" style="width: 152px" />
        <button v-else class="flex items-center gap-1 base-btn h-8 px-2 py-1.5 rounded-lg fade-in"
        :class="{'expanded': show.attributionWindow}"
        @click="show.attributionWindow = !show.attributionWindow">
          <BaseText type="label" size="sm" class="text-text-muted">
            {{ formatAttributionWindow }}
          </BaseText>
          <div class="transform transition-transform duration-200" :style="show.attributionWindow ? 'transform: scale(1, -1)' : ''">
            <ChevronIcon class-name="text-icon-normal" />
          </div>
        </button>
        <!-- Dropdown -->
        <transition name="fade">
          <div v-if="show.attributionWindow" class="absolute right-0 mt-1 border border-border-normal bg-white shadow-sm rounded-lg "
          style="width: 230px;z-index: 99999;">
            <div class="p-2">
              <div class="w-full text-center bg-background-normal rounded-md py-1.5 px-2.5">
                <BaseText class="text-text-muted" type="label" size="sm">
                  Attribution Window
                </BaseText>
              </div>
              <!-- Window inputs -->
              <div class="flex items-center gap-1 flex-col mt-2">
                <div class="flex w-full items-center justify-between z-20">
                  <BaseText size="sm" class="text-text-normal ml-1">
                    Click
                  </BaseText>
                  <div class="w-28">
                    <BaseSingleDropdown
                      :selected-obj="reportAttributionWindow.click"
                      :options="attributionWindowOptions"
                      option-key="label"
                      placeholder="Click"
                      use-parent-width
                      @change="updateWindow('click', $event)"
                    />
                  </div>
                </div>
                <div class="flex w-full items-center justify-between z-10">
                  <BaseText size="sm" class="text-text-normal ml-1">
                    View
                  </BaseText>
                  <div class="w-28">
                    <BaseSingleDropdown
                      :selected-obj="reportAttributionWindow.view"
                      :options="viewOptions"
                      placeholder="View"
                      option-key="label"
                      use-parent-width
                      @change="updateWindow('view', $event)"
                    />
                  </div>
                </div>
              </div>
            </div>
            <!-- Submit attr window changes -->
            <div class="flex w-full justify-end border-t p-2 border-border-normal">
              <button class="text-white black-btn-primary px-3 py-1 rounded-md"
              :disabled="disableWindowUpdate.disable" @click="submitNewWindow">
                <BaseText size="sm" type="label">
                  Apply
                </BaseText>
              </button>
            </div>
          </div>
        </transition>
      </div>
      <!-- Share Button (disabled on the demo Lens) -->
      <button class="flex text-white items-center gap-1 black-btn-primary p-1.5 pr-3 rounded-md h-8"
      :class="{ 'opacity-50 cursor-not-allowed': isDemoLens }" :disabled="isDemoLens"
      @click="$emit('showShareReport')">
        <ShareIcon />
        <BaseText type="label" size="sm">
          Share
        </BaseText>
      </button>
      <!-- Kebab Menu Button -->
      <div class="relative" v-on-clickaway="() => { show.optionsDropdown = false }">
        <button class="base-btn h-8 hover:bg-background-normal rounded-md transition-colors"
        :class="{'bg-background-normal' : show.optionsDropdown}" style="padding: 6px;"
        @click="show.optionsDropdown = !show.optionsDropdown">
          <KebabMenuIcon class="text-icon-normal" />
        </button>
        <!-- Dropdown -->
        <transition name="fade">
          <div v-if="show.optionsDropdown" style="z-index: 99999"
          class="p-1 absolute w-64 right-0 mt-1 border border-border-normal bg-white overflow-y-hidden dropdown-scrollable shadow-md rounded-lg">
            <!-- Options List -->
            <div v-for="opt in reportOptions" :key="opt.id">
              <button v-if="opt.action" class="group cursor-pointer flex items-center gap-2 rounded-lg p-1.5 transition duration-100 hover:bg-neutral-10 whitespace-nowrap w-full"
              :class="{'opacity-50 cursor-not-allowed hover:bg-transparent' : optionDisabledLabel(opt.id)}"
              @click="runOption(opt)">
                <component :is="opt.icon" class="text-icon-normal" />
                <BaseText type="label" size="sm" class="text-text-muted">
                  {{ opt.name }}
                </BaseText>
                <BaseText v-if="optionDisabledLabel(opt.id)" size="xs" class="text-text-disabled">
                  {{ optionDisabledLabel(opt.id) }}
                </BaseText>
              </button>
            </div>
          </div>
        </transition>
      </div>
    </div>
    <ConfirmDeleteModal
      v-if="show.deleteModal"
      delete-title="Are you sure you want to delete this report?"
      delete-description="Once deleted, reports can not be recovered."
      :loading="loadingDelete"
      @close="show.deleteModal = false"
      @confirm="deleteReport"
    />
    <EditReportModal
      v-if="show.editModal"
      :report-name="title"
      :report-desc="description"
      :report-id="reportId"
      @close="show.editModal = false"
      @update="updateLocalReport"
    />
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import LensAPI from '@/api/lens'
import { isDemoLensId } from '@/utils/lens/demo/lensDemoConstants'
import { mixin as clickaway } from 'vue-clickaway2'
// Components
import ConfirmDeleteModal from '../../globals/ConfirmDeleteModal.vue'
import EditReportModal from './EditReportModal.vue'
import BaseDropdownRouteSwitcher from '../../globals/BaseDropdownRouteSwitcher.vue'
import DataHealthButton from '../DataHealthButton.vue'

// Icons
import KebabMenuIcon from '../../globals/Icons/KebabMenuIcon.vue'
import ShareIcon from '../../globals/Icons/ShareIcon.vue'
import ReportIcon from '../../globals/Icons/LensIcons/ReportIcon.vue'
import ComparativeIcon from '../../globals/Icons/SidebarIcons/ComparativeIcon.vue'
import ChevronIcon from '../../globals/Icons/ChevronIcon.vue'
import RefreshIcon from '../../globals/Icons/LensIcons/RefreshIcon.vue'
import ExportCSVIcon from '../../globals/Icons/ExportCSVIcon.vue'
import DeleteIcon from '../../globals/Icons/DeleteIcon.vue'
import DuplicateIcon from '../../globals/Icons/DuplicateIcon.vue'
import EditIcon from '../../globals/Icons/EditIcon.vue'

export default {
  name: 'LensReportHeader',
  components: {
    // Components
    ConfirmDeleteModal,
    EditReportModal,
    DataHealthButton,
    // Icons
    ChevronIcon,
    RefreshIcon,
    ShareIcon,
    ReportIcon,
    ComparativeIcon,
    KebabMenuIcon,
    DeleteIcon,
    BaseDropdownRouteSwitcher
  },
  mixins: [clickaway],
  props: {
    reportId: {
      type: String,
      default: () => ''
    },
    loading: {
      type: Boolean,
      default: () => false
    },
    title: {
      type: String,
      default: () => ''
    },
    description: {
      type: String,
      default: () => ''
    },
    attributionWindow: {
      type: Object,
      default: () => {}
    },
    lens: {
      type: Object,
      default: () => {}
    },
    selectedDateRange: {
      type: Object,
      default: () => ({
        start: null,
        end: null
      })
    }
  },
  data () {
    const disabledBtns = ['export-pdf']
    const reportOptions = [
      {
        id: 'edit',
        icon: EditIcon,
        name: 'Edit Report',
        action: this.editReport
      },
      {
        id: 'duplicate',
        icon: DuplicateIcon,
        name: 'Copy Report',
        action: this.duplicateReport
      },
      {
        id: 'export-csv',
        icon: ExportCSVIcon,
        name: 'Export CSV',
        action: this.exportCSV
      },
      {
        id: 'export-pdf',
        icon: ExportCSVIcon,
        name: 'Export PDF',
        action: this.exportPDF
      },
      {
        id: 'delete-report',
        icon: DeleteIcon,
        name: 'Delete Report',
        action: this.showDeleteConfirm
      }
    ]

    const attributionWindowOptions = [
      { label: 'None', value: null },
      { label: '1 Day', value: '1d' },
      { label: '7 Days', value: '7d' },
      { label: '28 Day', value: '28d' }
    ]

    const viewOptions = [
      { label: 'None', value: null },
      { label: '1 Day', value: '1d' },
      { label: '7 Days', value: '7d' },
      { label: '28 Days', value: '28d' }
    ]
    return {
      reportOptions,
      disabledBtns,
      attributionWindowOptions,
      viewOptions,

      loadingDelete: false,

      reportAttributionWindow: {
        click: null,
        view: null
      },
      show: {
        optionsDropdown: false,
        duplicateModal: false,
        deleteModal: false,
        attributionWindow: false,
        editModal: false
      },

      shareButtonHovered: false
    }
  },
  computed: {
    isDemoLens () {
      return isDemoLensId(this.$route.params.lensId)
    },
    ...mapGetters('MetricsModule', ['getMetricsPlatform']),
    ...mapGetters('LensModule', ['getPendingLocalReportUpdate', 'getSidebarLenses']),
    ...mapGetters('RouterModule', ['getLastRoute']),
    formatAttributionWindow () {
      const click = this.attributionWindow.click ?? 'N/A'
      const view = this.attributionWindow.view ?? 'N/A'
      return `${click} Click, ${view} View`
    },
    disableWindowUpdate () {
      const isClickDiff = this.attributionWindow.click === this.reportAttributionWindow.click?.value
      const isViewDiff = this.attributionWindow.view === this.reportAttributionWindow.view?.value
      const areBothNull = this.reportAttributionWindow.click?.value === null && this.reportAttributionWindow.view?.value === null
      return {
        disable: (isClickDiff && isViewDiff) || areBothNull,
        click: isClickDiff,
        view: isViewDiff
      }
    },
    reportRoutes () {
      const sidebarLens = this.getSidebarLenses?.find((lens) => lens.id === this.$route.params.lensId)
      if (!sidebarLens) return [] // In cases where sidebarLenses are not loaded yet

      const lensReports = sidebarLens.folders.flatMap((folder) => folder.reports)
      return lensReports.map((report) => ({
        name: report.name,
        routeName: 'LensReport',
        params: { lensId: this.$route.params.lensId, reportId: report.id },
        icon: report.type === 'comparative' ? ComparativeIcon : ReportIcon
      }))
    },
    currentRoute () {
      return this.reportRoutes.find((route) => route.params.reportId === this.reportId)
    }
  },
  watch: {
    attributionWindow (newWindow) {
      if (this.loading) return
      const click = this.attributionWindowOptions.find((opt) => opt.value === newWindow.click)
      const view = this.viewOptions.find((opt) => opt.value === newWindow.view)
      this.reportAttributionWindow = {
        click, view
      }
    },
    getPendingLocalReportUpdate (newUpdate) {
      if (newUpdate?.id && newUpdate?.data) {
        // If report metadata is changed in sidebar, we need to update the report in the report view and refresh routes
        if (newUpdate.id === this.reportId) {
          this.$emit('localReportUpdate', newUpdate.data)
        }
      }
    }
  },
  methods: {
    ...mapActions('LensModule', ['fetchSidebarLenses']),
    // Edit / copy / delete are off on the demo Lens
    optionDisabledLabel (optionId) {
      if (this.disabledBtns.includes(optionId)) return '(Coming soon)'
      if (this.isDemoLens && ['edit', 'duplicate', 'delete-report'].includes(optionId)) return '(Not in demo)'
      return null
    },
    runOption (option) {
      if (this.isDemoLens && this.optionDisabledLabel(option.id)) return
      option.action()
    },
    updateWindow (key, val) {
      this.reportAttributionWindow[key] = val
    },
    updateLocalReport (event) {
      this.$emit('localReportUpdate', event)
      this.show.editModal = false
    },
    submitNewWindow () {
      const click = this.reportAttributionWindow.click?.value
      const view = this.reportAttributionWindow.view?.value

      if (!this.disableWindowUpdate.click) {
        this.$emit('changeWindow', { type: 'click_attribution_window', value: click })
      }
      if (!this.disableWindowUpdate.view) {
        this.$emit('changeWindow', { type: 'view_attribution_window', value: view })
      }

      this.show.attributionWindow = false
    },
    navigateBack () {
      // If there is a previous history entry, go back
      if (this.getLastRoute) {
        this.$router.go(-1)
      } else {
        // Otherwise, go to the Lens Dashboard
        const currentLens = this.$route.params.lensId
        this.$router.push({ name: 'LensDashboardView', params: { lensId: currentLens } })
      }
    },
    duplicateReport () {
      this.show.optionsDropdown = false
      this.$emit('showDuplicateReport')
    },
    editReport () {
      this.show.editModal = true
    },
    exportCSV () {
      this.show.optionsDropdown = false
      this.$emit('exportCSV')
    },
    exportPDF () {
      // this.$showAlert({
      //   type: 'success',
      //   message: '(MOCK): mock pdf'
      // })
    },
    showDeleteConfirm () {
      this.show.optionsDropdown = false
      this.show.deleteModal = true
    },
    async deleteReport () {
      this.loadingDelete = true
      console.log('%c Deleting Report', 'color: pink; font-size: 14px;')
      try {
        await LensAPI.LensReport.remove(this.reportId)
        await this.fetchSidebarLenses()
      } catch (e) {
        this.$showAlert({
          type: 'error',
          message: 'Deleting report error'
        })
      } finally {
        this.loadingDelete = false
        this.$showAlert({
          type: 'success',
          message: 'Report successfully deleted'
        })
        this.$router.push({ name: 'LensView' })
      }
    },
    handleRouteChange (route) {
      // Handle route change logic here
      console.log('Route selected:', route)
    },
    getInitials (name) {
      return name.split(' ').map(word => word[0]).join('').toUpperCase().substring(0, 2)
    }
  }
}
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: all 150ms !important;
}

.fade-enter,
.fade-leave-to .fade-leave-active {
  opacity: 0;
  transform: translateY(-1rem);
}

.fade-in {
  animation: fadeIn 150ms ease-in-out;
}
@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}
.share-report-btn:disabled{
  color: #A4ACB9;
  background-color: #F6F8FA;
  box-shadow: none;
  cursor:not-allowed
}

.base-btn {
  box-shadow: 0px 1px 2px 0px rgba(0, 56, 108, 0.08), 0px 0px 0px 1px rgba(0, 56, 108, 0.08);
  transition: box-shadow 100ms ease-in-out, background-color 100ms ease-in-out;
}

.base-btn:hover, .base-btn.expanded {
  box-shadow: 0px 1px 2px 0px rgba(0, 56, 108, 0.12), 0px 0px 0px 1px rgba(0, 56, 108, 0.12);
}

.truncate-group-tooltip{
  background: #24252D;
  bottom: calc(-100% + 0px);
  opacity: 0;
  z-index: 99999999 !important;
  transition: all 150ms;
  pointer-events:none;

  left: 0;
  transform: translateY(0);
}

.truncate-group-trigger:hover + .truncate-group-tooltip {
  transform: translateY(-14px) !important;
  opacity: 1 !important;
}
</style>